import { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { Spinner, Button } from "react-bootstrap";
import { getTransactionByAccountId } from '../../service/api/TransactionService';
import useExecutionNotices from '../../service/Hooks/useExecutionNotices';
import { FaFilePdf, FaEye, FaDownload, FaEnvelope } from 'react-icons/fa';

// Composant pour l'en-tête du modal
const TransactionModalHeader = ({ title, onClose }) => (
  <div className="modal-header">
    <h5 className="modal-title">{title}</h5>
    <button type="button" className="btn-close" onClick={onClose}></button>
  </div>
);

// Composant pour le corps du modal avec chargement, erreur ou table
const TransactionModalBody = ({ 
  loading, 
  error, 
  transactions, 
  formatDate, 
  onExecutionNotice,
  onDownloadNotice, 
  onSendNotice,
  hasExecutionNotice,
  getExecutionNotice,
  noticeLoading
}) => {
  if (loading) {
    return (
      <div className="text-center">
        <Spinner animation="border" variant="primary" />
        <p>Chargement des transactions...</p>
      </div>
    );
  }

  if (error) {
    return <p className="text-danger">Erreur: {error}</p>;
  }

  if (transactions.length === 0) {
    return <p className="tex text-muted text-center">Aucune transaction trouvée pour ce compte.</p>;
  }

  return (
    <div className="table-responsive">
      <table className="table table-striped align-middle">
        <thead>
          <tr>
            <th>Order ID</th>
            <th>Type Ordre</th>
            <th>Créateur</th>
            <th>Emetteur</th>
            <th>Destinataire</th>
            <th>Quantité</th>
            <th>Prix Unitaire</th>
            <th>Prix Total</th>
            <th>Status</th>
            <th>Date de Création</th>
            <th>Avis d'Exécution</th>
          </tr>
        </thead>
        <tbody>
          {transactions.map((transaction) => {
            const hasNotice = hasExecutionNotice(transaction.id);
            const notice = getExecutionNotice(transaction.id);
            
            return (
              <tr key={transaction.id}>
                <td className="text-center">{transaction.orderId}</td>
                <td className="text-center">
                  <span className={`badge rounded-pill bg-${transaction.side === "Buy" ? "bg-success bg-opacity-10 text-success border border-success border-opacity-25 rounded-pill px-2 py-2" : "bg-danger bg-opacity-10 text-success border border-success border-opacity-25 rounded-pill px-2 py-2"}`}>
                    {transaction.side}
                  </span>
                </td>
                <td>{transaction.creatorName}</td>
                <td>{transaction.fromAccountEmail}</td>
                <td>{transaction.toAccountEmail}</td>
                <td className="text-center">{transaction.quantity}</td>
                <td className="text-end text-nowrap">
                  <span className="counter-amount-badge" >
                    {new Intl.NumberFormat("fr-FR", { 
                      style: "decimal", 
                      minimumFractionDigits: 0, 
                      maximumFractionDigits: 0, 
                      useGrouping: true 
                    }).format(transaction.price || 0)} FCFA
                  </span>
                </td>
                <td>
                  <span className="counter-amount-badge">
                    {new Intl.NumberFormat("fr-FR", { 
                      style: "decimal", 
                      minimumFractionDigits: 0, 
                      maximumFractionDigits: 0, 
                      useGrouping: true 
                    }).format(transaction.totalPrice || 0)} FCFA
                  </span>
                </td>
                <td>
                  <span className="bg-success bg-opacity-10 text-success border border-success border-opacity-25 rounded-pill px-2 py-2">
                    {transaction.status}
                  </span>
                </td>
                <td className="text-center">{formatDate(transaction.creationDate)}</td>
                <td className="text-center">
                  {transaction.status === 'Completed' && (
                    <div className="btn-group btn-group-sm">
                      {/* Bouton principal : Générer/Voir */}
                      <Button
                        variant={hasNotice ? "outline-info" : "outline-primary"}
                        size="sm"
                        onClick={() => onExecutionNotice(transaction.id)}
                        disabled={noticeLoading}
                        title={hasNotice ? "Voir l'avis" : "Générer l'avis"}
                      >
                        {noticeLoading ? (
                          <div className="spinner-border spinner-border-sm" role="status">
                            <span className="visually-hidden">Chargement...</span>
                          </div>
                        ) : hasNotice ? (
                          <FaEye />
                        ) : (
                          <FaFilePdf />
                        )}
                      </Button>
                      
                      {/* Boutons secondaires (seulement si avis existe) */}
                      {hasNotice && (
                        <>
                          <Button
                            variant="outline-success"
                            size="sm"
                            onClick={() => onDownloadNotice(transaction.id)}
                            title="Télécharger"
                          >
                            <FaDownload />
                          </Button>
                          <Button
                            variant="outline-warning"
                            size="sm"
                            onClick={() => onSendNotice(transaction.id)}
                            title="Envoyer par email"
                          >
                            <FaEnvelope />
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// Composant pour la pagination
const TransactionPagination = ({ currentPage, totalPages, setCurrentPage }) => (
  <nav>
    <ul className="pagination justify-content-center mt-3">
      <li className={`page-item ${currentPage <= 1 ? "disabled" : ""}`}>
        <button className="page-link" onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}>
          <i className="fa-solid fa-angle-left"></i>
        </button>
      </li>
      {Array.from({ length: totalPages }, (_, index) => {
        const pageNumber = index + 1;
        return (
          <li key={pageNumber} className={`page-item ${currentPage === pageNumber ? "active" : ""}`}>
            <button className="page-link" onClick={() => setCurrentPage(pageNumber)}>
              {pageNumber}
            </button>
          </li>
        );
      })}
      <li className={`page-item ${currentPage >= totalPages ? "disabled" : ""}`}>
        <button className="page-link" onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}>
          <i className="fa-solid fa-angle-right"></i>
        </button>
      </li>
    </ul>
  </nav>
);

// Composant principal TransactionClient
const TransactionClient = ({ fromAccountId, domainId, onClose }) => {
  const [transactions, setTransactions] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const pageSize = 7;

  // Hook pour les avis d'exécution - chargement automatique
  const { 
    loading: noticeLoading, 
    executionNotices,
    generateNotice, 
    downloadNotice, 
    viewNotice, 
    sendNoticeByEmail,
    hasExecutionNotice,
    getExecutionNotice
  } = useExecutionNotices(domainId, fromAccountId);

  useEffect(() => {
    if (!fromAccountId || !domainId) return;
    const fetchTransactions = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getTransactionByAccountId(fromAccountId, domainId, currentPage, pageSize);
        setTransactions(data.items || []);
        setTotalPages(data.totalPages || 1);
      } catch (err) {
        setError(err.message || "Impossible de récupérer les transactions.");
      } finally {
        setLoading(false);
      }
    };
    fetchTransactions();
  }, [fromAccountId, domainId, currentPage]);

  // Fonction unique pour gérer la génération/accès aux avis
  const handleExecutionNotice = async (transactionId) => {
    if (!domainId) return;

    const hasNotice = hasExecutionNotice(transactionId);
    
    if (hasNotice) {
      // Si l'avis existe déjà, on peut directement le visualiser
      const notice = getExecutionNotice(transactionId);
      await viewNotice(domainId, notice.id);
    } else {
      // Sinon, on le génère (et il sera automatiquement disponible après génération)
      await generateNotice(domainId, transactionId);
    }
  };

  // Fonctions directes pour les actions
  const handleDownloadNotice = async (transactionId) => {
    if (!domainId) return;
    
    const notice = getExecutionNotice(transactionId);
    if (notice) {
      await downloadNotice(domainId, notice.id, `avis-${notice.noticeNumber}.pdf`);
    }
  };

  const handleSendNoticeByEmail = async (transactionId) => {
    if (!domainId) return;
    
    const notice = getExecutionNotice(transactionId);
    if (notice) {
      await sendNoticeByEmail(domainId, notice.id);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { day: "numeric", month: "short", year: "numeric" };
    return date.toLocaleDateString("fr-FR", options);
  };

  if (!fromAccountId || !domainId) return null;

  return ReactDOM.createPortal(
    <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,0.5)", display: "block" }}>
      <div className="modal-dialog modal-dialog-centered modal-custom">
        <div className="modal-content">
          <TransactionModalHeader title="Liste des Transactions" onClose={onClose} />
          <div className="modal-body">
            <TransactionModalBody 
              loading={loading} 
              error={error} 
              transactions={transactions} 
              formatDate={formatDate}
              onExecutionNotice={handleExecutionNotice}
              onDownloadNotice={handleDownloadNotice}
              onSendNotice={handleSendNoticeByEmail}
              hasExecutionNotice={hasExecutionNotice}
              getExecutionNotice={getExecutionNotice}
              noticeLoading={noticeLoading}
            />
            {!loading && !error && transactions.length > 0 && (
              <TransactionPagination 
                currentPage={currentPage} 
                totalPages={totalPages} 
                setCurrentPage={setCurrentPage} 
              />
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TransactionClient;